import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { getAudioUrl, getAudioMarks, getAvailableVoices } from '../services/audioService';
import { findCurrentVerse } from '../hooks/useVerseTracking';
import {
  startAmbient, stopAmbient, setAmbientVolume,
  pauseAmbient, resumeAmbient, isAmbientRunning,
} from '../services/ambientSoundService';
import { egwAudioService } from '../services/egwPlanService';

export const AudioPlayerContext = createContext(null);

const DEFAULT_VOICE = 'en-US-Chirp3-HD-Charon';

// Book abbreviation map for audioService queries
const BOOK_ABBREV_MAP = {
  'Genesis': 'GEN', 'Exodus': 'EXO', 'Leviticus': 'LEV', 'Numbers': 'NUM',
  'Deuteronomy': 'DEU', 'Joshua': 'JOS', 'Judges': 'JDG', 'Ruth': 'RUT',
  '1 Samuel': '1SA', '2 Samuel': '2SA', '1 Kings': '1KI', '2 Kings': '2KI',
  '1 Chronicles': '1CH', '2 Chronicles': '2CH', 'Ezra': 'EZR', 'Nehemiah': 'NEH',
  'Esther': 'EST', 'Job': 'JOB', 'Psalms': 'PSA', 'Proverbs': 'PRO',
  'Ecclesiastes': 'ECC', 'Song of Solomon': 'SNG', 'Isaiah': 'ISA',
  'Jeremiah': 'JER', 'Lamentations': 'LAM', 'Ezekiel': 'EZK', 'Daniel': 'DAN',
  'Hosea': 'HOS', 'Joel': 'JOE', 'Amos': 'AMO', 'Obadiah': 'OBA',
  'Jonah': 'JON', 'Micah': 'MIC', 'Nahum': 'NAH', 'Habakkuk': 'HAB',
  'Zephaniah': 'ZEP', 'Haggai': 'HAG', 'Zechariah': 'ZEC', 'Malachi': 'MAL',
  'Matthew': 'MAT', 'Mark': 'MRK', 'Luke': 'LUK', 'John': 'JHN',
  'Acts': 'ACT', 'Romans': 'ROM', '1 Corinthians': '1CO', '2 Corinthians': '2CO',
  'Galatians': 'GAL', 'Ephesians': 'EPH', 'Philippians': 'PHP', 'Colossians': 'COL',
  '1 Thessalonians': '1TH', '2 Thessalonians': '2TH', '1 Timothy': '1TI',
  '2 Timothy': '2TI', 'Titus': 'TIT', 'Philemon': 'PHM', 'Hebrews': 'HEB',
  'James': 'JAS', '1 Peter': '1PE', '2 Peter': '2PE', '1 John': '1JN',
  '2 John': '2JN', '3 John': '3JN', 'Jude': 'JUD', 'Revelation': 'REV',
};

// Chapter counts for navigation bounds
const CHAPTER_COUNTS = {
  'Genesis': 50, 'Exodus': 40, 'Leviticus': 27, 'Numbers': 36, 'Deuteronomy': 34,
  'Joshua': 24, 'Judges': 21, 'Ruth': 4, '1 Samuel': 31, '2 Samuel': 24,
  '1 Kings': 22, '2 Kings': 25, '1 Chronicles': 29, '2 Chronicles': 36,
  'Ezra': 10, 'Nehemiah': 13, 'Esther': 10, 'Job': 42, 'Psalms': 150,
  'Proverbs': 31, 'Ecclesiastes': 12, 'Song of Solomon': 8, 'Isaiah': 66,
  'Jeremiah': 52, 'Lamentations': 5, 'Ezekiel': 48, 'Daniel': 12,
  'Hosea': 14, 'Joel': 3, 'Amos': 9, 'Obadiah': 1, 'Jonah': 4,
  'Micah': 7, 'Nahum': 3, 'Habakkuk': 3, 'Zephaniah': 3, 'Haggai': 2,
  'Zechariah': 14, 'Malachi': 4, 'Matthew': 28, 'Mark': 16, 'Luke': 24,
  'John': 21, 'Acts': 28, 'Romans': 16, '1 Corinthians': 16, '2 Corinthians': 13,
  'Galatians': 6, 'Ephesians': 6, 'Philippians': 4, 'Colossians': 4,
  '1 Thessalonians': 5, '2 Thessalonians': 3, '1 Timothy': 6, '2 Timothy': 4,
  'Titus': 3, 'Philemon': 1, 'Hebrews': 13, 'James': 5, '1 Peter': 5,
  '2 Peter': 3, '1 John': 5, '2 John': 1, '3 John': 1, 'Jude': 1, 'Revelation': 22,
};

const BOOK_ORDER = Object.keys(BOOK_ABBREV_MAP);

export function AudioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const retryCountRef = useRef(0);
  const currentUrlRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [loopMode, setLoopMode] = useState('none'); // 'none' | 'chapter' | 'verse'
  const [autoAdvance, setAutoAdvance] = useState(true); // default ON
  const [chapterComplete, setChapterComplete] = useState(false);
  const [activeVerse, setActiveVerse] = useState(null);
  const [activeBook, setActiveBook] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeVoice, setActiveVoice] = useState(DEFAULT_VOICE);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [marks, setMarks] = useState([]);
  const [hasChunkOffsetBug, setHasChunkOffsetBug] = useState(false);

  // ── Ambient sound state ─────────────────────────────────────────────────
  const [ambientEnabled, setAmbientEnabled] = useState(false);
  const [ambientType, setAmbientType] = useState('forest_wind'); // default to first track
  const [chapterVolume, setChapterVolume] = useState(0.8);  // 0–1
  const [ambientVolume, setAmbientVolume_] = useState(0.4); // 0–1

  // ── EGW Player mode ──────────────────────────────────────────────────────────
  const [egwMode, setEgwMode] = useState(false);
  const [egwBookCode, setEgwBookCode] = useState(null);
  const [egwSequenceNumber, setEgwSequenceNumber] = useState(null);

  // ── Chapter navigation callback (set by BibleReader to sync text) ───────
  // Signature: (book: string, chapter: number) => void
  const onChapterChangeRef = useRef(null);

  const registerChapterChangeCallback = useCallback((fn) => {
    onChapterChangeRef.current = fn;
  }, []);

  // ── Refs mirror all mutable state so event handlers never go stale ──────
  const loopModeRef = useRef(loopMode);
  const autoAdvanceRef = useRef(autoAdvance);
  const activeVerseRef = useRef(activeVerse);
  const marksRef = useRef(marks);
  const activeBookRef = useRef(activeBook);
  const activeChapterRef = useRef(activeChapter);
  const activeVoiceRef = useRef(activeVoice);
  const isPlayingRef = useRef(isPlaying);
  const isLoadedRef = useRef(isLoaded);
  const ambientEnabledRef = useRef(ambientEnabled);
  const ambientTypeRef = useRef(ambientType);
  const ambientVolumeRef = useRef(ambientVolume);

  useEffect(() => { loopModeRef.current = loopMode; }, [loopMode]);
  useEffect(() => { autoAdvanceRef.current = autoAdvance; }, [autoAdvance]);
  useEffect(() => { activeVerseRef.current = activeVerse; }, [activeVerse]);
  useEffect(() => { marksRef.current = marks; }, [marks]);
  useEffect(() => { activeBookRef.current = activeBook; }, [activeBook]);
  useEffect(() => { activeChapterRef.current = activeChapter; }, [activeChapter]);
  useEffect(() => { activeVoiceRef.current = activeVoice; }, [activeVoice]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { isLoadedRef.current = isLoaded; }, [isLoaded]);
  useEffect(() => { ambientEnabledRef.current = ambientEnabled; }, [ambientEnabled]);
  useEffect(() => { ambientTypeRef.current = ambientType; }, [ambientType]);
  useEffect(() => { ambientVolumeRef.current = ambientVolume; }, [ambientVolume]);

  // nextChapterInternal ref — needed inside the ended handler
  const nextChapterInternalRef = useRef(null);

  // ── Single audio element, single set of listeners, attached ONCE ────────
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      const t = audio?.currentTime;
      setCurrentTime(t);
      const verse = findCurrentVerse(marksRef?.current, t);
      if (verse !== null && verse !== activeVerseRef?.current) {
        setActiveVerse(verse);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(audio?.duration || 0);
      setIsLoaded(true);
      setIsLoading(false);
      setLoadError(false);
      setChapterComplete(false);
      retryCountRef.current = 0;
    };

    const handleEnded = () => {
      setIsPlaying(false);
      // Stop ambient when chapter ends (unless looping)
      if (loopModeRef?.current === 'chapter') {
        audio.currentTime = 0;
        audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
      } else if (loopModeRef?.current === 'verse') {
        const verseMarks = marksRef?.current?.filter(m => !m?.is_chunk_mark);
        const verseStart = verseMarks?.find(m => m?.verse === activeVerseRef?.current);
        if (verseStart) {
          audio.currentTime = verseStart?.time_seconds;
          audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
        }
      } else if (autoAdvanceRef?.current) {
        nextChapterInternalRef?.current?.();
      } else {
        // Chapter complete, auto-advance off
        setChapterComplete(true);
        if (ambientEnabledRef?.current) {
          stopAmbient(true);
        }
      }
    };

    const handleError = async () => {
      if (retryCountRef?.current < 1 && currentUrlRef?.current) {
        retryCountRef.current += 1;
        const bookAbbrev = BOOK_ABBREV_MAP?.[activeBookRef?.current];
        if (bookAbbrev) {
          const result = await getAudioUrl(bookAbbrev, activeChapterRef?.current, activeVoiceRef?.current);
          if (result?.url) {
            currentUrlRef.current = result?.url;
            audio.src = result?.url;
            audio?.load();
          }
        }
      } else {
        setIsLoading(false);
        setLoadError(true);
      }
    };

    audio?.addEventListener('timeupdate', handleTimeUpdate);
    audio?.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio?.addEventListener('ended', handleEnded);
    audio?.addEventListener('error', handleError);

    return () => {
      audio?.removeEventListener('timeupdate', handleTimeUpdate);
      audio?.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio?.removeEventListener('ended', handleEnded);
      audio?.removeEventListener('error', handleError);
      audio?.pause();
      audio.src = '';
      stopAmbient(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync chapter volume to audio element ────────────────────────────────
  useEffect(() => {
    if (audioRef?.current) {
      audioRef.current.volume = chapterVolume;
    }
  }, [chapterVolume]);

  // ── Ambient: start/stop when enabled/type changes ───────────────────────
  // Ambient only plays when audio is playing
  useEffect(() => {
    if (ambientEnabled && isPlaying) {
      startAmbient(ambientType, ambientVolume);
    } else if (!ambientEnabled) {
      stopAmbient(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambientEnabled, ambientType]);

  // ── Ambient: pause/resume with audio playback ────────────────────────────
  useEffect(() => {
    if (!ambientEnabled) return;
    if (isPlaying) {
      resumeAmbient();
      if (!isAmbientRunning()) {
        startAmbient(ambientType, ambientVolume);
      }
    } else {
      pauseAmbient();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, ambientEnabled]);

  const loadAudio = useCallback(async (book, chapter, voice) => {
    const audio = audioRef?.current;
    if (!audio) return;

    const bookAbbrev = BOOK_ABBREV_MAP?.[book];
    if (!bookAbbrev) return;

    setIsLoading(true);
    setIsLoaded(false);
    setLoadError(false);
    setActiveVerse(null);
    setCurrentTime(0);
    setDuration(0);
    setHasChunkOffsetBug(false);
    setChapterComplete(false);
    retryCountRef.current = 0;

    audio?.pause();
    setIsPlaying(false);

    // Fetch voices
    const voices = await getAvailableVoices(bookAbbrev, chapter);
    setAvailableVoices(voices);

    // Determine which voice to use
    let resolvedVoice = voice;
    if (voices?.length > 0 && !voices?.includes(voice)) {
      const neural2Voice = voices?.find(v => v?.includes('Neural2'));
      resolvedVoice = neural2Voice || voices?.[0];
    }

    // Fetch audio URL
    const result = await getAudioUrl(bookAbbrev, chapter, resolvedVoice);
    if (!result) {
      setIsLoading(false);
      setLoadError(true);
      return;
    }

    // Fetch marks
    const { marks: rawMarks, hasChunkOffsetBug: bugDetected } = await getAudioMarks(bookAbbrev, chapter, resolvedVoice);
    const sortedMarks = [...(rawMarks || [])]?.sort((a, b) => a?.time_seconds - b?.time_seconds);
    setMarks(sortedMarks);
    setHasChunkOffsetBug(bugDetected || false);

    currentUrlRef.current = result?.url;
    audio.src = result?.url;
    audio.playbackRate = playbackSpeed;
    audio.volume = chapterVolume;
    audio?.load();

    setActiveBook(book);
    setActiveChapter(chapter);
    setActiveVoice(resolvedVoice);

    // Notify Bible Reader to sync text
    if (onChapterChangeRef?.current) {
      onChapterChangeRef?.current(book, chapter);
    }
  }, [playbackSpeed, chapterVolume]);

  const nextChapterInternal = useCallback(async () => {
    const book = activeBookRef?.current;
    const chapter = activeChapterRef?.current;
    const voice = activeVoiceRef?.current;
    if (!book || !chapter) return;

    const maxChapters = CHAPTER_COUNTS?.[book] || 1;
    if (chapter < maxChapters) {
      await loadAudio(book, chapter + 1, voice);
      const audio = audioRef?.current;
      if (audio) {
        audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
      }
    } else {
      const bookIdx = BOOK_ORDER?.indexOf(book);
      if (bookIdx < BOOK_ORDER?.length - 1) {
        const nextBook = BOOK_ORDER?.[bookIdx + 1];
        await loadAudio(nextBook, 1, voice);
        const audio = audioRef?.current;
        if (audio) {
          audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
        }
      }
    }
  }, [loadAudio]);

  // Keep the ref in sync so the ended handler can call it
  useEffect(() => {
    nextChapterInternalRef.current = nextChapterInternal;
  }, [nextChapterInternal]);

  // Actions
  const openPlayer = useCallback(async (book, chapter) => {
    if (book !== activeBook || chapter !== activeChapter) {
      await loadAudio(book, chapter, activeVoice);
    }
    setIsOpen(true);
  }, [activeBook, activeChapter, activeVoice, loadAudio]);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const play = useCallback(() => {
    const audio = audioRef?.current;
    if (!audio || !isLoaded) return;
    audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
  }, [isLoaded]);

  const pause = useCallback(() => {
    const audio = audioRef?.current;
    if (!audio) return;
    audio?.pause();
    setIsPlaying(false);
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef?.current;
    if (!audio) return;
    audio?.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
    if (ambientEnabledRef?.current) stopAmbient(true);
  }, []);

  const seek = useCallback((timeSeconds) => {
    const audio = audioRef?.current;
    if (!audio) return;
    audio.currentTime = timeSeconds;
    setCurrentTime(timeSeconds);
  }, []);

  const seekToVerse = useCallback((verseNumber) => {
    const verseMarks = marks?.filter(m => !m?.is_chunk_mark);
    const verseMark = verseMarks?.find(m => m?.verse === verseNumber);
    if (verseMark) {
      seek(verseMark?.time_seconds);
    }
  }, [marks, seek]);

  const setSpeed = useCallback((rate) => {
    const audio = audioRef?.current;
    if (audio) audio.playbackRate = rate;
    setPlaybackSpeed(rate);
  }, []);

  const setLoopModeAction = useCallback((mode) => {
    setLoopMode(mode);
  }, []);

  const setAutoAdvanceAction = useCallback((val) => {
    setAutoAdvance(val);
  }, []);

  const setVoice = useCallback(async (voiceName) => {
    if (voiceName === activeVoice) return;
    const wasPlaying = isPlaying;
    await loadAudio(activeBook, activeChapter, voiceName);
    if (wasPlaying) {
      const audio = audioRef?.current;
      if (audio) audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
    }
  }, [activeVoice, activeBook, activeChapter, isPlaying, loadAudio]);

  const nextChapter = useCallback(async () => {
    if (!activeBook || !activeChapter) return;
    const maxChapters = CHAPTER_COUNTS?.[activeBook] || 1;
    const wasPlaying = isPlaying;
    if (activeChapter < maxChapters) {
      await loadAudio(activeBook, activeChapter + 1, activeVoice);
    } else {
      const bookIdx = BOOK_ORDER?.indexOf(activeBook);
      if (bookIdx < BOOK_ORDER?.length - 1) {
        await loadAudio(BOOK_ORDER?.[bookIdx + 1], 1, activeVoice);
      }
    }
    if (wasPlaying) {
      const audio = audioRef?.current;
      if (audio) audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
    }
  }, [activeBook, activeChapter, activeVoice, isPlaying, loadAudio]);

  const prevChapter = useCallback(async () => {
    if (!activeBook || !activeChapter) return;
    const wasPlaying = isPlaying;
    if (activeChapter > 1) {
      await loadAudio(activeBook, activeChapter - 1, activeVoice);
    } else {
      const bookIdx = BOOK_ORDER?.indexOf(activeBook);
      if (bookIdx > 0) {
        const prevBook = BOOK_ORDER?.[bookIdx - 1];
        const prevBookChapters = CHAPTER_COUNTS?.[prevBook] || 1;
        await loadAudio(prevBook, prevBookChapters, activeVoice);
      }
    }
    if (wasPlaying) {
      const audio = audioRef?.current;
      if (audio) audio?.play()?.then(() => setIsPlaying(true))?.catch(() => {});
    }
  }, [activeBook, activeChapter, activeVoice, isPlaying, loadAudio]);

  const rewind = useCallback(() => {
    const audio = audioRef?.current;
    if (!audio) return;
    const newTime = Math.max(0, audio?.currentTime - 10);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, []);

  const fastForward = useCallback(() => {
    const audio = audioRef?.current;
    if (!audio) return;
    const newTime = Math.min(audio?.duration || 0, audio?.currentTime + 10);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, []);

  // ── Ambient controls ─────────────────────────────────────────────────────
  const toggleAmbient = useCallback((enabled) => {
    setAmbientEnabled(enabled);
  }, []);

  const changeAmbientType = useCallback((type) => {
    setAmbientType(type);
    if (ambientEnabledRef?.current && isPlayingRef?.current) {
      startAmbient(type, ambientVolumeRef?.current);
    }
  }, []);

  const updateAmbientVolume = useCallback((vol) => {
    setAmbientVolume_(vol);
    setAmbientVolume(vol);
  }, []);

  const updateChapterVolume = useCallback((vol) => {
    setChapterVolume(vol);
    if (audioRef?.current) {
      audioRef.current.volume = vol;
    }
  }, []);

  const isAtStart = activeBook === 'Genesis' && activeChapter === 1;

  const openEgwPlayer = useCallback(async (bookCode, sequenceNumber, voice) => {
    const voiceToUse = voice || activeVoice;
    setEgwMode(true);
    setEgwBookCode(bookCode);
    setEgwSequenceNumber(sequenceNumber);
    setIsLoading(true);
    setIsLoaded(false);
    setLoadError(false);
    setCurrentTime(0);
    setDuration(0);
    setMarks([]);

    try {
      const { data: audioFile } = await egwAudioService?.getAudioFile(bookCode, sequenceNumber, voiceToUse);
      if (!audioFile) {
        setIsLoading(false);
        setLoadError(true);
        setIsOpen(true);
        return;
      }

      const signedUrl = await egwAudioService?.getSignedUrl(audioFile?.mp3_path);
      if (!signedUrl) {
        setIsLoading(false);
        setLoadError(true);
        setIsOpen(true);
        return;
      }

      const { data: pageMarks } = await egwAudioService?.getPageMarks(audioFile?.id);
      // Convert page marks to the same format as bible verse marks
      const convertedMarks = (pageMarks || [])?.map(m => ({
        ref: `${bookCode}.PAGE.${m?.page_number}`,
        book: bookCode,
        chapter: sequenceNumber,
        verse: m?.page_number,
        is_chunk_mark: false,
        time_seconds: m?.time_seconds,
      }));
      setMarks(convertedMarks);
      setActiveVoice(voiceToUse);

      const audio = audioRef?.current;
      if (audio) {
        audio?.pause();
        setIsPlaying(false);
        currentUrlRef.current = signedUrl;
        audio.src = signedUrl;
        audio.playbackRate = playbackSpeed;
        audio.volume = chapterVolume;
        audio?.load();
      }
    } catch (err) {
      console.error('openEgwPlayer error:', err);
      setIsLoading(false);
      setLoadError(true);
    }

    setIsOpen(true);
  }, [activeVoice, playbackSpeed, chapterVolume]);

  const value = {
    isOpen,
    isPlaying,
    isLoaded,
    isLoading,
    loadError,
    currentTime,
    duration,
    playbackSpeed,
    loopMode,
    autoAdvance,
    chapterComplete,
    activeVerse,
    activeBook,
    activeChapter,
    activeVoice,
    availableVoices,
    marks,
    hasChunkOffsetBug,
    audioRef,
    isAtStart,
    // Ambient
    ambientEnabled,
    ambientType,
    chapterVolume,
    ambientVolume,
    // EGW Player
    egwMode,
    egwBookCode,
    egwSequenceNumber,
    openEgwPlayer,
    // Actions
    openPlayer,
    closeDrawer,
    play,
    pause,
    stop,
    seek,
    seekToVerse,
    setSpeed,
    setLoopMode: setLoopModeAction,
    setAutoAdvance: setAutoAdvanceAction,
    setVoice,
    nextChapter,
    prevChapter,
    rewind,
    fastForward,
    toggleAmbient,
    setAmbientType: changeAmbientType,
    updateAmbientVolume,
    updateChapterVolume,
    registerChapterChangeCallback,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

function useAudioPlayerContext() {
  return useContext(AudioPlayerContext);
}

export { useAudioPlayerContext };